
import React, { useState } from "react";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaCalendarDay,
  FaCalendarTimes,
  FaFilter,
  FaUserTie,
  FaClock,
  FaEdit,
  FaTrashAlt,
  FaCheck,
  FaInbox,
  FaExclamationCircle,
} from "react-icons/fa";

// Funções utilitárias de formatação — mantidas.
const formatarData = (data) => {
  try {
    const utcDate = new Date(data);

    if (isNaN(utcDate.getTime())) {
      throw new Error("Data inválida");
    }

    const localDate = new Date(
      utcDate.getTime() + utcDate.getTimezoneOffset() * 60000
    );

    return localDate.toLocaleDateString("pt-BR");
  } catch (erro) {
    console.error("Erro ao formatar data:", erro);
    return "Data inválida";
  }
};

const formatarHora = (hora) => {
  if (!hora) return "Não informado";

  const [hours, minutes] = hora.split(":");
  return `${hours}:${minutes}`;
};

const formatarTelefone = (telefone) => {
  if (!telefone) return "Não informado";

  const padraoTelefone = /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/;

  if (padraoTelefone.test(telefone)) return telefone;

  return telefone.replace(/(\d{2})(\d{4,5})(\d{4})/, "($1) $2-$3");
};

// Ajuste de data — mantido.
const adjustToLocalDate = (dateString) => {
  const date = new Date(dateString);

  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

// Classificação de datas — mantida.
const isToday = (dateString) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const agendamentoDate = adjustToLocalDate(dateString);

    if (isNaN(agendamentoDate.getTime())) return false;

    agendamentoDate.setHours(0, 0, 0, 0);

    return agendamentoDate.getTime() === today.getTime();
  } catch {
    return false;
  }
};

const isPast = (dateString) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const agendamentoDate = adjustToLocalDate(dateString);

    if (isNaN(agendamentoDate.getTime())) return false;

    agendamentoDate.setHours(0, 0, 0, 0);

    return agendamentoDate.getTime() < today.getTime();
  } catch {
    return false;
  }
};

const secoesAgendamento = [
  {
    chave: "passados",
    titulo: "Agendamentos passados",
    descricao: "Atendimentos de datas anteriores.",
    icone: FaCalendarTimes,
    classe: "is-past",
  },
  {
    chave: "hoje",
    titulo: "Agendamentos de hoje",
    descricao: "Atendimentos previstos para hoje.",
    icone: FaCalendarDay,
    classe: "is-today",
  },
  {
    chave: "futuros",
    titulo: "Próximos agendamentos",
    descricao: "Atendimentos das próximas datas.",
    icone: FaCalendarCheck,
    classe: "is-future",
  },
];

const AgendamentosList = ({
  agendamentos,
  erro,
  navigate,
  handleExcluirAgendamento,
  handleMarkAsCompleted,
}) => {
  const [filtroFuncionario, setFiltroFuncionario] = useState("");

  const funcionariosUnicos = [
    ...new Set(
      agendamentos
        .map((agendamento) => agendamento.funcionario_nome)
        .filter(Boolean)
    ),
  ];

  const agendamentosFiltrados = filtroFuncionario
    ? agendamentos.filter(
        (agendamento) =>
          agendamento.funcionario_nome === filtroFuncionario
      )
    : agendamentos;

  const agendamentosDeHoje = agendamentosFiltrados.filter(
    (agendamento) => isToday(agendamento.data)
  );

  const agendamentosFuturos = agendamentosFiltrados.filter(
    (agendamento) =>
      !isToday(agendamento.data) && !isPast(agendamento.data)
  );

  const agendamentosPassados = agendamentosFiltrados.filter(
    (agendamento) => isPast(agendamento.data)
  );

  const secoes = {
    passados: agendamentosPassados,
    hoje: agendamentosDeHoje,
    futuros: agendamentosFuturos,
  };

  const nenhumAgendamento =
    agendamentosPassados.length === 0 &&
    agendamentosDeHoje.length === 0 &&
    agendamentosFuturos.length === 0;

  const formatarPreco = (preco) => {
    if (preco === null || preco === undefined || preco === "") {
      return "Não informado";
    }

    // Preserva preços que já chegam formatados pela API.
    if (typeof preco === "string" && /[R$]/.test(preco)) {
      return preco;
    }

    const valor = Number(preco);

    if (!Number.isFinite(valor)) return String(preco);

    return valor.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const renderTabela = (titulo, lista, secao) => {
    if (lista.length === 0) return null;

    const IconeSecao = secao.icone;

    return (
      <section
        className={`ag-list-section ${secao.classe}`}
        key={secao.chave}
      >
        <div className="ag-list-section-heading">
          <div className="ag-list-section-icon">
            <IconeSecao />
          </div>

          <div className="ag-list-section-copy">
            <h3>{titulo}</h3>
            <p>{secao.descricao}</p>
          </div>

          <span className="ag-list-count">
            {lista.length} {lista.length === 1 ? "agendamento" : "agendamentos"}
          </span>
        </div>

        <div className="ag-list-table-viewport">
          <table className="ag-list-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Telefone</th>
                <th>Serviço</th>
                <th>Preço</th>
                <th>Profissional</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Status</th>
                <th className="ag-list-actions-heading">Ações</th>
              </tr>
            </thead>

            <tbody>
              {lista.map((agendamento) => (
                <tr key={agendamento.id}>
                  <td
                    className="ag-list-client"
                    title={agendamento.cliente_nome || "Não informado"}
                  >
                    <span className="ag-list-primary-text">
                      {agendamento.cliente_nome || "Não informado"}
                    </span>
                  </td>

                  <td title={formatarTelefone(agendamento.telefone_celular)}>
                    {formatarTelefone(agendamento.telefone_celular)}
                  </td>

                  <td title={agendamento.servico_nome || "Não informado"}>
                    {agendamento.servico_nome || "Não informado"}
                  </td>

                  <td className="ag-list-price">
                    {formatarPreco(agendamento.servico_preco)}
                  </td>

                  <td title={agendamento.funcionario_nome || "Não informado"}>
                    <span className="ag-list-professional">
                      <FaUserTie />
                      {agendamento.funcionario_nome || "Não informado"}
                    </span>
                  </td>

                  <td>{agendamento.data ? formatarData(agendamento.data) : "Não informado"}</td>

                  <td>
                    <span className="ag-list-time">
                      <FaClock />
                      {formatarHora(agendamento.hora)}
                    </span>
                  </td>

                  <td>
                    <span
                      className="ag-list-status"
                      title={agendamento.status || "Não informado"}
                    >
                      <span className="ag-list-status-dot" />
                      {agendamento.status || "Não informado"}
                    </span>
                  </td>

                  <td>
                    <div className="ag-list-actions">
                      <button
                        type="button"
                        className="ag-list-action ag-list-action-edit"
                        onClick={() =>
                          navigate(`/agendamentos/edit/${agendamento.id}`)
                        }
                        title="Editar agendamento"
                        aria-label={`Editar agendamento de ${agendamento.cliente_nome || "cliente"}`}
                      >
                        <FaEdit />
                      </button>

                      <button
                        type="button"
                        className="ag-list-action ag-list-action-delete"
                        onClick={() =>
                          handleExcluirAgendamento(agendamento.id)
                        }
                        title="Excluir agendamento"
                        aria-label={`Excluir agendamento de ${agendamento.cliente_nome || "cliente"}`}
                      >
                        <FaTrashAlt />
                      </button>

                      <button
                        type="button"
                        className="ag-list-action ag-list-action-complete"
                        onClick={() =>
                          handleMarkAsCompleted(agendamento.id)
                        }
                        title="Concluir agendamento"
                        aria-label={`Concluir agendamento de ${agendamento.cliente_nome || "cliente"}`}
                      >
                        <FaCheck />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  return (
    <div className="ag-list">
      <div className="ag-list-header">
        <div className="ag-list-total">
          <span className="ag-list-total-label">Total encontrado</span>
          <strong>{agendamentosFiltrados.length}</strong>
        </div>
      </div>

      {erro && (
        <div className="ag-list-error" role="alert">
          <FaExclamationCircle />
          <span>{erro}</span>
        </div>
      )}

      <div className="ag-list-filters">
        <div className="ag-list-filter-label">
          <FaFilter />
          <label htmlFor="filtroFuncionario">Filtrar por profissional</label>
        </div>

        <select
          id="filtroFuncionario"
          value={filtroFuncionario}
          onChange={(event) => setFiltroFuncionario(event.target.value)}
          className="ag-list-select"
        >
          <option value="">Todos os profissionais</option>

          {funcionariosUnicos.map((funcionario) => (
            <option key={funcionario} value={funcionario}>
              {funcionario}
            </option>
          ))}
        </select>
      </div>

      {nenhumAgendamento ? (
        <div className="ag-list-empty">
          <div className="ag-list-empty-icon">
            <FaInbox />
          </div>

          <h3>Nenhum agendamento encontrado</h3>

          <p>
            {filtroFuncionario
              ? "Não há agendamentos para o profissional selecionado."
              : "Quando houver agendamentos, eles aparecerão nesta área."}
          </p>
        </div>
      ) : (
        <div className="ag-list-sections">
          {secoesAgendamento.map((secao) =>
            renderTabela(
              secao.titulo,
              secoes[secao.chave],
              secao
            )
          )}
        </div>
      )}
    </div>
  );
};

export default AgendamentosList;
